/**
 * @file pages/DevLogin.tsx
 * @brief Login and forgot-password flow for NestTask App.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Lock, Mail, Eye, EyeOff, KeyRound, ArrowLeft } from 'lucide-react';
import Cookies from 'js-cookie';

import { useAppDispatch } from '@/store';
import { setCredentials } from '@/store/slices/authSlice';
import {
  login,
  mapBackendUserToFrontend,
  requestPasswordReset,
  verifyResetOtp,
  resetPassword,
} from '@/services/api/authService';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';

type AuthStep = 'login' | 'otp' | 'reset';

const passwordRules = (password: string): string[] => {
  const errors: string[] = [];
  if (password.length < 12) errors.push('At least 12 characters');
  if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
  if (!/[0-9]/.test(password)) errors.push('One number');
  if (!/[^A-Za-z0-9]/.test(password)) errors.push('One special character');
  return errors;
};

const DevLogin: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useSnackbar();

  const [step, setStep] = useState<AuthStep>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const goToLogin = () => {
    setStep('login');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setPassword('');
  };

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const payload = await login(email, password);
      const user = mapBackendUserToFrontend(payload.user);
      Cookies.set('authToken', payload.accessToken, { expires: 1 });
      if (payload.refreshToken) {
        Cookies.set('refreshToken', payload.refreshToken, { expires: 7 });
      }
      Cookies.set('userRole', payload.user.role, { expires: 30 });
      Cookies.set('email', payload.user.email, { expires: 30 });
      dispatch(setCredentials({ user, token: payload.accessToken }));
      const redirectTo = (location.state as { from?: string } | null)?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      showError('Enter your email first');
      return;
    }
    setLoading(true);
    try {
      const message = await requestPasswordReset(email.trim());
      showSuccess(message);
      setOtp('');
      setStep('otp');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      await verifyResetOtp(email.trim(), otp.trim());
      showSuccess('Code verified. Set a new password.');
      setNewPassword('');
      setConfirmPassword('');
      setStep('reset');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const errors = passwordRules(newPassword);
    if (errors.length > 0) {
      showError(`Password needs: ${errors.join(', ')}`);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const message = await resetPassword(email.trim(), otp.trim(), newPassword, confirmPassword);
      showSuccess(message);
      goToLogin();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: '#f8fafc',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background:
          'radial-gradient(circle at top left, #d6d7d9 0%, transparent 40%), radial-gradient(circle at bottom right, #ececed 0%, transparent 42%), #f8fafc',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            NestTask App
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
            {step === 'login' && 'Sign in to continue'}
            {step === 'otp' && 'Enter the code we emailed you'}
            {step === 'reset' && 'Choose a new password'}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            bgcolor: 'rgba(255,255,255,0.92)',
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
          }}
        >
          {step === 'login' && (
            <Box
              component="form"
              onSubmit={handleLogin}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                type="email"
                autoComplete="email"
                required
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={18} color="#94a3b8" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={18} color="#94a3b8" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} type="button">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                <Button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
                >
                  Forgot password?
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ py: 1.35, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Sign in'}
              </Button>
            </Box>
          )}

          {step === 'otp' && (
            <Box
              component="form"
              onSubmit={handleVerifyOtp}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                We sent a 6-digit code to <strong>{email}</strong>
              </Typography>
              <TextField
                label="OTP code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                required
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyRound size={18} color="#94a3b8" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || otp.length < 6}
                sx={{ py: 1.35, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Verify code'}
              </Button>
              <Button type="button" onClick={handleForgotPassword} disabled={loading} sx={{ textTransform: 'none' }}>
                Resend code
              </Button>
              <Button
                type="button"
                startIcon={<ArrowLeft size={16} />}
                onClick={goToLogin}
                sx={{ textTransform: 'none' }}
              >
                Back to sign in
              </Button>
            </Box>
          )}

          {step === 'reset' && (
            <Box
              component="form"
              onSubmit={handleResetPassword}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                helperText="Min 12 characters, with uppercase, lowercase, number, and special character"
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={18} color="#94a3b8" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNewPassword(!showNewPassword)} type="button">
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Repeat new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                error={confirmPassword.length > 0 && confirmPassword !== newPassword}
                helperText={
                  confirmPassword.length > 0 && confirmPassword !== newPassword ? 'Passwords do not match' : ' '
                }
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={18} color="#94a3b8" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ py: 1.35, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Reset password'}
              </Button>
              <Button
                type="button"
                startIcon={<ArrowLeft size={16} />}
                onClick={goToLogin}
                sx={{ textTransform: 'none' }}
              >
                Back to sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default DevLogin;
